import { useEffect, useState, type FunctionComponent } from "react";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { useOutletContext } from "react-router-dom";
import moment, { type Moment } from "moment-timezone";

import { type IncidentReport, type IncidentReportDeliveryHoursEntry } from "components/CriticalIncident/interfaces";
import { useThrottledSectionUpdate } from "../../hooks/useThrottledSectionUpdate";
import { FormControlWrapper } from "components/Shared";
import { TextBox, DateInput, Select, Button } from "components/Shared/Inputs";
import { ReportSectionAccordion } from "components/CriticalIncident/components/Shared/ReportSectionAccordion";
import { ChooseYesNo } from "components/CriticalIncident/components/ChooseYesNo";
import { EMPTY_ENTRY, MAX_DELIVERY_HOURS_ENTRIES } from "./schema";

import "./DeliveryHours.scss";

const MAX_CHARS = 1000;
const MAX_DURATION_HOURS = 24;

const FEE_LINE_OPTIONS: { value: string; labelKey: string }[] = [
  { value: 'Admin Hours', labelKey: 'fee_line_admin_hours' },
  { value: 'Travel Hours', labelKey: 'fee_line_travel_hours' },
  { value: 'Delivery', labelKey: 'fee_line_delivery' },
  { value: 'Follow Up Support', labelKey: 'fee_line_follow_up_support' },
];

export const DeliveryHours: FunctionComponent = () => {
  const { t, ready: translationsReady } = useTranslation('criticalIncident', { useSuspense: false });
  const { values, handleChange, touched, errors, setFieldValue } = useFormikContext<IncidentReport>();
  const { isReadonly = false } = useOutletContext<{ isReadonly: boolean }>();
  const throttledUpdateReport = useThrottledSectionUpdate('deliveryHours', values.deliveryHours);
  const [expandedEntryIndex, setExpandedEntryIndex] = useState<number | false>(0);
  // durationHours is stored in Formik as a parsed number, so a controlled input driven straight
  // off that value snaps a trailing "." (or trailing zeros) back to its canonical numeric string
  // on every keystroke — e.g. typing "10." immediately re-renders as "10", silently dropping the
  // period the user just typed, which then makes the next digit look like it's appending onto
  // "10" instead of "10.". Tracking the raw in-progress text separately (cleared on blur, once
  // editing is done) keeps the displayed value in sync with what was actually typed.
  const [editingDuration, setEditingDuration] = useState<{ index: number; raw: string } | null>(null);

  useEffect(() => {
    if (!values.deliveryHours?.entries?.length) {
      const entries = [{ ...EMPTY_ENTRY }];
      setFieldValue('deliveryHours.entries', entries);
      throttledUpdateReport({ entries });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddEntry = () => {
    const entries = values.deliveryHours?.entries ?? [];
    if (entries.length >= MAX_DELIVERY_HOURS_ENTRIES) return;

    const nextEntries = [...entries, { ...EMPTY_ENTRY }];
    setFieldValue('deliveryHours.entries', nextEntries);
    throttledUpdateReport({ entries: nextEntries });
    setExpandedEntryIndex(nextEntries.length - 1);
  };

  const handleRemoveEntry = (index: number) => {
    const entries = values.deliveryHours?.entries.filter((_, i) => i !== index);
    setFieldValue('deliveryHours.entries', entries);
    throttledUpdateReport({ entries });
    // remaining entries shift down by one index, which would otherwise leave any in-progress
    // duration draft pointing at the wrong (now-renumbered) entry
    setEditingDuration(null);
  };

  const updateEntryField = (index: number, field: keyof IncidentReportDeliveryHoursEntry, value: unknown) => {
    const entries = values.deliveryHours?.entries.map((entry, i) => i === index ? { ...entry, [field]: value } : entry);
    throttledUpdateReport({ entries });
  };

  const makeEntryChangeHandler = (index: number, field: keyof IncidentReportDeliveryHoursEntry) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleChange(event);
      updateEntryField(index, field, event.target.value);
    };

  const handleEntryDateChange = (index: number) => (value: Moment | null) => {
    const isoString = value?.toDate().toISOString();
    setFieldValue(`deliveryHours.entries[${index}].date`, isoString);
    updateEntryField(index, 'date', isoString);
  };

  // durationHours is stored in Formik as a parsed number (see the `editingDuration` comment
  // above) — TextBox's `decimal-2` type handles all the character-level validation (digits, one
  // decimal point, 2-decimal-place cap, no +/-/e/E beyond a single leading "-", the max-24 keydown
  // guard via `slotProps.input.max` below); this handler only owns keeping the raw typed string
  // as the visible draft and deriving the number to store.
  const handleDurationChange = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    setEditingDuration({ index, raw });
    const parsed = Number(raw);
    const value = raw === '' || Number.isNaN(parsed) ? null : parsed;
    setFieldValue(`deliveryHours.entries[${index}].durationHours`, value);
    updateEntryField(index, 'durationHours', value);
  };

  const handleDurationBlur = () => setEditingDuration(null);

  const handleEntryCompletedChange = (index: number) => (value: boolean | null) => {
    setFieldValue(`deliveryHours.entries[${index}].completed`, value);
    updateEntryField(index, 'completed', value);
  };

  return translationsReady && (
    <div className="delivery_hours">
      <h2>{t('deliveryHours')}</h2>
      <Grid container spacing={2}>
        {values.deliveryHours?.entries?.map((entry, index) => {
          const entryTouched = touched.deliveryHours?.entries?.[index] as Record<string, unknown> | undefined;
          const entryErrors = errors?.deliveryHours?.entries?.[index] as Record<string, string> | undefined;

          return (
            <Grid item sm={12} key={index}>
              <ReportSectionAccordion
                label={t('delivery_hours_entry_number', { number: index + 1 })}
                name={`deliveryHours.entries[${index}]`}
                pristineValue={EMPTY_ENTRY}
                removeAction={isReadonly ? undefined : () => handleRemoveEntry(index)}
                removeHidden={(values.deliveryHours?.entries.length ?? 0) <= 1}
                expanded={expandedEntryIndex === index}
                onToggle={(isExpanded) => setExpandedEntryIndex(isExpanded ? index : false)}
                readonly={isReadonly}
              >
                <Grid container spacing={2}>
                  <Grid item sm={6}>
                    <FormControlWrapper
                      isReadonly={isReadonly}
                      type="date"
                      dateFormat="L"
                      label={t('date')}
                      name={`deliveryHours.entries[${index}].date`}
                    >
                      <DateInput
                        label={t('date')}
                        required
                        name={`deliveryHours.entries[${index}].date`}
                        onChange={handleEntryDateChange(index)}
                        value={entry.date ? moment(entry.date) : null}
                        error={Boolean(entryTouched?.date) && Boolean(entryErrors?.date)}
                        helperText={entryTouched?.date ? t(entryErrors?.date || ' ') : ''}
                        fullWidth
                      />
                    </FormControlWrapper>
                  </Grid>
                  <Grid item sm={6}>
                    <FormControlWrapper
                      isReadonly={isReadonly}
                      label={t('duration_hours')}
                      name={`deliveryHours.entries[${index}].durationHours`}
                      fullWidth
                    >
                      <TextBox
                        type="decimal-2"
                        label={t('duration_hours')}
                        required
                        name={`deliveryHours.entries[${index}].durationHours`}
                        value={editingDuration?.index === index ? editingDuration.raw : (entry.durationHours ?? '')}
                        onChange={handleDurationChange(index)}
                        onBlur={handleDurationBlur}
                        slotProps={{ input: { min: 0, max: MAX_DURATION_HOURS } }}
                        error={Boolean(entryTouched?.durationHours) && Boolean(entryErrors?.durationHours)}
                        helperText={entryTouched?.durationHours ? t(entryErrors?.durationHours || ' ') : ''}
                      />
                    </FormControlWrapper>
                  </Grid>
                  <Grid item sm={12}>
                    <FormControlWrapper
                      isReadonly={isReadonly}
                      label={t('activity')}
                      name={`deliveryHours.entries[${index}].activity`}
                      fullWidth
                    >
                      <TextBox
                        multiline
                        minRows={4}
                        maxRows={6}
                        label={t('activity')}
                        required
                        name={`deliveryHours.entries[${index}].activity`}
                        value={entry.activity}
                        onChange={makeEntryChangeHandler(index, 'activity')}
                        slotProps={{ input: { maxLength: MAX_CHARS } }}
                        error={Boolean(entryTouched?.activity) && Boolean(entryErrors?.activity)}
                        helperText={entryTouched?.activity ? t(entryErrors?.activity || ' ') : ''}
                      />
                    </FormControlWrapper>
                  </Grid>
                  <Grid item sm={12}>
                    <FormControlWrapper
                      isReadonly={isReadonly}
                      type="select"
                      fullWidth
                      label={`${t('fee_line')}*`}
                      name={`deliveryHours.entries[${index}].feeLine`}
                      required
                      options={FEE_LINE_OPTIONS.map(option => ({ value: option.value, label: t(option.labelKey) }))}
                      error={Boolean(entryTouched?.feeLine) && Boolean(entryErrors?.feeLine)}
                      helperText={entryTouched?.feeLine ? t(entryErrors?.feeLine || ' ') : ''}
                    >
                      <Select
                        name={`deliveryHours.entries[${index}].feeLine`}
                        value={entry.feeLine ?? ''}
                        onChange={(event) => {
                          handleChange(event);
                          updateEntryField(index, 'feeLine', event.target.value as string);
                        }}
                      >
                        {FEE_LINE_OPTIONS.map(option => (
                          <MenuItem value={option.value} key={option.value}>{t(option.labelKey)}</MenuItem>
                        ))}
                      </Select>
                    </FormControlWrapper>
                  </Grid>
                  <Grid item sm={12}>
                    <FormControlWrapper
                      isReadonly={isReadonly}
                      type="radio.yesno"
                      label={`${t('completed')}*`}
                      name={`deliveryHours.entries[${index}].completed`}
                      fullWidth
                      error={Boolean(entryTouched?.completed) && Boolean(entryErrors?.completed)}
                      helperText={entryTouched?.completed ? t(entryErrors?.completed || ' ') : ''}
                    >
                      <ChooseYesNo
                        label={`${t('completed')}*`}
                        name={`deliveryHours.entries[${index}].completed`}
                        value={entry.completed}
                        onChange={handleEntryCompletedChange(index)}
                      />
                    </FormControlWrapper>
                  </Grid>
                </Grid>
              </ReportSectionAccordion>
            </Grid>
          );
        })}

        {!isReadonly && (values.deliveryHours?.entries.length ?? 0) < MAX_DELIVERY_HOURS_ENTRIES && (
          <Grid item sm={12}>
            <Button variant="text" className="delivery_hours__add-button" onClick={handleAddEntry}>
              <AddIcon fontSize="small" />
              {t('add_delivery_hours_entry')}
            </Button>
          </Grid>
        )}
      </Grid>
    </div>
  );
};
